import { useState } from 'react';
import { Lock } from 'lucide-react';
import type { TeamMemberProfileDto } from '../../../../domain/contracts/api.contracts';
import { DelinquencyStatus } from '../../../../domain/enums/SystemEnums';
import { useTeamMemberInstallments } from '../../../../application/hooks/team/useTeamQueries';
import { useTranslation } from '../../../../application/hooks/useTranslation';
import { formatDate } from '../../../../domain/formatters/date';
import { QueryState } from '../../../components/feedback/QueryState';
import { AmountDisplay } from '../../../components/ui/AmountDisplay';
import { Badge } from '../../../components/ui/Badge';
import { ProgressRing } from '../../../components/ui/ProgressRing';
import { TENOR } from '../../../../domain/calculators/installments';
import { STATUS_TONE } from '../teamFormat';
import { InstallmentMiniGrid } from './InstallmentMiniGrid';

const PAY_TONE = { VERIFIED: 'success', PENDING: 'warning', REJECTED: 'danger' } as const;

/** Read-only installment view of a team member (E24) — no receipts, no payment actions. */
export function InstallmentsTab({ userId, profile }: { userId: string; profile: TeamMemberProfileDto }) {
  const { t, locale } = useTranslation();
  const hasSubscriptions = profile.subscriptions.length > 0;
  const [subId, setSubId] = useState<string | undefined>(profile.subscriptions[0]?.subscriptionId);
  const data = useTeamMemberInstallments(userId, subId, hasSubscriptions);
  const closed = profile.card.status === DelinquencyStatus.CLOSED;

  if (!hasSubscriptions) {
    return (
      <div className="m360-inst" data-testid="member360-installments">
        <div
          className="m360-no-subscriptions"
          data-testid="member360-no-subscriptions"
          style={{
            padding: '32px 16px',
            textAlign: 'center',
            color: 'var(--color-text-muted, #64748b)',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 500 }}>
            {t('team.noSubscriptions') ?? 'This team member has no plot subscriptions.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="m360-inst" data-testid="member360-installments">
      {profile.subscriptions.length > 1 && (
        <label className="tm-select m360-plot">
          <span>{t('team.plot')}</span>
          <select value={subId} onChange={(e) => setSubId(e.target.value)} data-testid="member360-plot">
            {profile.subscriptions.map((s) => (
              <option key={s.subscriptionId} value={s.subscriptionId}>
                {s.subscriptionId}
              </option>
            ))}
          </select>
        </label>
      )}
      {closed && (
        <p className="m360-frozen">
          <Lock size={13} aria-hidden /> {t('team.frozen')}
        </p>
      )}

      {!data.data ? (
        <QueryState loading={data.isPending} error={data.error} onRetry={() => void data.refetch()} skeletonCount={3} />
      ) : (
        <>
          <section className="m360-summary">
            <ProgressRing
              value={data.data.subscription.clearedInstallments}
              max={TENOR}
              size={92}
            />
            <dl className="m360-summary-grid">
              <div>
                <dt>{t('team.plot')}</dt>
                <dd>{data.data.subscription.subscriptionId}</dd>
              </div>
              <div>
                <dt>{t('team.monthly')}</dt>
                <dd><AmountDisplay amount={data.data.subscription.monthlyAmountBDT} /></dd>
              </div>
              <div>
                <dt>{t('team.nextDue')}</dt>
                <dd>
                  {formatDate(data.data.subscription.nextDueDate, locale)}
                  {data.data.subscription.nextDueAmountBDT > 0 && (
                    <> · <AmountDisplay amount={data.data.subscription.nextDueAmountBDT} /></>
                  )}
                </dd>
              </div>
              <div>
                <dt>{t('team.advance')}</dt>
                <dd><AmountDisplay amount={data.data.subscription.advanceBalanceBDT} /></dd>
              </div>
              <div>
                <dt>{t('team.overdueMonths')}</dt>
                <dd className={data.data.subscription.overdueMonths > 0 ? 'text-danger' : undefined}>
                  <span className="tabular">{data.data.subscription.overdueMonths}</span>
                </dd>
              </div>
              <div>
                <dt>{t('team.arrears')}</dt>
                <dd><AmountDisplay amount={data.data.subscription.arrearsBDT} bold={data.data.subscription.arrearsBDT > 0} /></dd>
              </div>
            </dl>
            <div className="m360-summary-foot">
              <Badge tone={STATUS_TONE[data.data.subscription.status]}>{t(`team.status_${data.data.subscription.status}`)}</Badge>
              <span>
                {t('team.paidOf', { n: data.data.subscription.clearedInstallments, total: 120 })}
              </span>
            </div>
          </section>

          <h3 className="m360-h3">{t('team.gridTitle')}</h3>
          {data.data.cells.length === 0 ? (
            <div
              className="m360-no-schedule"
              data-testid="member360-no-schedule"
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: 'var(--color-text-muted, #64748b)',
                background: 'var(--color-bg-secondary, #f8fafc)',
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <p style={{ margin: '0 0 6px 0', fontWeight: 600, fontSize: '0.9375rem' }}>
                {t('installments.scheduleNotGeneratedTitle')}
              </p>
              <p style={{ margin: 0, fontSize: '0.8125rem' }}>
                {t('installments.scheduleNotGeneratedDesc')}
              </p>
            </div>
          ) : (
            <InstallmentMiniGrid cells={data.data.cells} />
          )}

          <h3 className="m360-h3">{t('team.recentPayments')}</h3>
          {(data.data.recentPayments ?? []).length === 0 ? (
            <p className="m360-hint">{t('team.noPayments')}</p>
          ) : (
            <ul className="m360-payments" data-testid="member360-payments">
              {(data.data.recentPayments ?? []).map((p, i) => (
                <li key={`${p.date}-${i}`}>
                  <span>
                    <strong><AmountDisplay amount={p.amountBDT} /></strong>
                    <small>{formatDate(p.date, locale)} · {t(`team.ch_${p.channel}`)}</small>
                  </span>
                  <Badge tone={PAY_TONE[p.status]}>{t(`team.pay_${p.status}`)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
